/**
 * CampaignMapScene — two-layer campaign navigation.
 *
 * WORLD MAP: shows 5 level nodes on a path. Click a level to enter it.
 * STAGE MAP: shows 10 fight nodes within the selected level.
 */

import Phaser from 'phaser'
import {
  LEVELS, WORLD_OPPONENTS, STAGES_PER_LEVEL,
  CampaignProgress,
  loadCampaignProgress, resetCampaignProgress, applyCampaignResult,
} from '../data/campaign'
import { isTouchDevice } from '../input/TouchController'

// ─── Layout constants ────────────────────────────────────────────────────────

const _MOBILE = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
const NODE_R = _MOBILE ? 42 : 36

// Shared X positions for 5-column serpentine layout
const COL_X = [130, 350, 570, 790, 1010]

// Both world map and stage map use serpentine: 5 top + 5 bottom
const WORLD_Y1 = 280
const WORLD_Y2 = 510
const STAGE_Y1 = 280
const STAGE_Y2 = 510

function serpentinePos(i: number, y1: number, y2: number): { x: number; y: number } {
  if (i < 5) return { x: COL_X[i],     y: y1 }
  else       return { x: COL_X[9 - i], y: y2 }
}

function worldPos(i: number): { x: number; y: number } {
  return serpentinePos(i, WORLD_Y1, WORLD_Y2)
}

function stagePos(i: number): { x: number; y: number } {
  return serpentinePos(i, STAGE_Y1, STAGE_Y2)
}

const SPEED_COLOR: Record<string, string> = {
  slow: '#4ECB71', normal: '#4CC9F0', fast: '#FFE566', turbo: '#FF6B6B',
}

// ─── Scene ──────────────────────────────────────────────────────────────────

export class CampaignMapScene extends Phaser.Scene {
  private progress!: CampaignProgress
  private viewMode: 'world' | 'stages' = 'world'
  private selectedLevel = -1

  constructor() { super({ key: 'CampaignMapScene' }) }

  preload(): void {
    this.load.image('mapamundi', 'assets/mapamundi.png')
    for (let w = 1; w <= 10; w++) {
      const wp = String(w).padStart(2, '0')
      this.load.image(`map_${wp}`, `assets/map_${wp}.png`)
      for (let s = 1; s <= 10; s++) {
        const sp = String(s).padStart(2, '0')
        this.load.image(`villain_${wp}_${sp}`, `assets/villain_${wp}_${sp}.png`)
      }
    }
  }

  init(data: {
    result?: { level: number; stage: number; won: boolean; score: number };
    view?: 'world' | 'stages';
    level?: number;
  }): void {
    if (data?.result) {
      const { level, stage, won, score } = data.result
      this.progress = applyCampaignResult(level, stage, won, score)
      this.viewMode = 'stages'
      this.selectedLevel = level
    } else {
      this.progress = loadCampaignProgress()
      this.viewMode = data?.view ?? 'world'
      this.selectedLevel = data?.level ?? -1
    }
  }

  create(): void {
    if (this.viewMode === 'stages' && this.selectedLevel >= 0) {
      this.createStageMap()
    } else {
      this.createWorldMap()
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WORLD MAP — shows 5 level nodes
  // ═══════════════════════════════════════════════════════════════════════════

  private createWorldMap(): void {
    const W = this.scale.width
    const H = this.scale.height

    if (this.textures.exists('mapamundi')) {
      const bg = this.add.image(W / 2, H / 2, 'mapamundi')
      bg.setDisplaySize(W, H)
      this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.52)
    } else {
      this.add.rectangle(W / 2, H / 2, W, H, 0x0a0a1a)
    }

    // Header
    this.add.text(W / 2, 50, 'CAMPAIGN', {
      fontFamily: 'monospace', fontSize: '32px',
      color: '#FFE566', stroke: '#000', strokeThickness: 4,
    }).setOrigin(0.5)

    this.add.text(W / 2, 90, 'Defeat all 10 guardians in each realm to advance!', {
      fontFamily: 'monospace', fontSize: '12px', color: '#555577',
    }).setOrigin(0.5)

    // Total score
    this.add.text(W / 2, 120, `TOTAL SCORE: ${this.progress.totalScore.toLocaleString()}`, {
      fontFamily: 'monospace', fontSize: '16px', color: '#4CC9F0',
    }).setOrigin(0.5)

    // Campaign complete banner
    if (this.progress.currentLevel >= LEVELS.length) {
      this.add.text(W / 2, 155, 'CAMPAIGN COMPLETE!', {
        fontFamily: 'monospace', fontSize: '20px', color: '#4ECB71',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5)
    }

    // Path lines between level nodes
    const pathGfx = this.add.graphics()
    for (let i = 0; i < LEVELS.length - 1; i++) {
      const a = worldPos(i)
      const b = worldPos(i + 1)
      const completed = i < this.progress.currentLevel
      pathGfx.lineStyle(8, 0x080810)
      pathGfx.lineBetween(a.x, a.y, b.x, b.y)
      pathGfx.lineStyle(5, completed ? 0x1a3a28 : 0x0f0f1e)
      pathGfx.lineBetween(a.x, a.y, b.x, b.y)
    }

    // Level nodes
    for (let i = 0; i < LEVELS.length; i++) {
      this.drawLevelNode(i)
    }

    // Player marker
    const ci = Math.min(this.progress.currentLevel, LEVELS.length - 1)
    const cp = worldPos(ci)
    const marker = this.add.graphics().setDepth(5)
    marker.lineStyle(3, 0xffffff, 0.9)
    marker.strokeCircle(cp.x, cp.y, NODE_R + 10)
    this.tweens.add({ targets: marker, alpha: 0.3, yoyo: true, repeat: -1, duration: 600 })

    // Back button
    const mob = isTouchDevice()
    const back = this.add.text(24, 50, '< BACK', {
      fontFamily: 'monospace', fontSize: mob ? '20px' : '14px', color: '#555577',
      padding: mob ? { x: 12, y: 8 } : { x: 6, y: 3 },
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true })
    back.on('pointerover', () => back.setColor('#aaaacc'))
    back.on('pointerout',  () => back.setColor('#555577'))
    back.on('pointerdown', () => this.scene.start('LobbyScene'))

    // Reset link
    const reset = this.add.text(W - 24, H - 16, 'Reset progress', {
      fontFamily: 'monospace', fontSize: '11px', color: '#222244',
    }).setOrigin(1, 1).setInteractive({ useHandCursor: true })
    reset.on('pointerover', () => reset.setColor('#ff6666'))
    reset.on('pointerout',  () => reset.setColor('#222244'))
    reset.on('pointerdown', () => { resetCampaignProgress(); this.scene.restart() })
  }

  private drawLevelNode(index: number): void {
    const lvl       = LEVELS[index]
    const { x, y }  = worldPos(index)
    const p         = this.progress
    const completed = index < p.currentLevel
    const isCurrent = index === p.currentLevel
    const locked    = index > p.currentLevel

    const gfx = this.add.graphics().setDepth(2)

    // Circle
    if (completed) {
      gfx.fillStyle(0x1a3a28); gfx.fillCircle(x, y, NODE_R)
      gfx.lineStyle(3, 0x4ECB71); gfx.strokeCircle(x, y, NODE_R)
    } else if (isCurrent) {
      gfx.fillStyle(0x1a1a3a); gfx.fillCircle(x, y, NODE_R)
      gfx.lineStyle(3, lvl.color); gfx.strokeCircle(x, y, NODE_R)
    } else {
      gfx.fillStyle(0x0d0d1e); gfx.fillCircle(x, y, NODE_R)
      gfx.lineStyle(2, 0x1a1a3a); gfx.strokeCircle(x, y, NODE_R)
    }

    // World map thumbnail (inside node circle)
    const mapKey = `map_${String(index + 1).padStart(2, '0')}`
    if (!locked && this.textures.exists(mapKey)) {
      const r = NODE_R - 2
      const thumb = this.add.image(x, y, mapKey)
      thumb.setDisplaySize(r * 2, r * 2)
      thumb.setDepth(2)
      if (locked) thumb.setAlpha(0.25)
      const maskGfx = this.make.graphics({ x: x - r, y: y - r })
      maskGfx.fillStyle(0xffffff)
      maskGfx.fillCircle(r, r, r)
      thumb.setMask(maskGfx.createGeometryMask())
    }

    // Level number (only when locked, i.e. no thumbnail)
    const numColor = completed ? '#4ECB71' : isCurrent ? '#ffffff' : '#333355'
    if (locked) {
      this.add.text(x, y, `${index + 1}`, {
        fontFamily: 'monospace', fontSize: '24px',
        color: numColor, stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(3)
    }

    // Level name below
    const nameColor = completed ? '#3a8a50' : isCurrent ? '#ddddff' : '#222244'
    this.add.text(x, y + NODE_R + 16, lvl.name, {
      fontFamily: 'monospace', fontSize: '12px', color: nameColor,
    }).setOrigin(0.5).setDepth(3)

    // Speed badge
    const speedColor = locked ? '#1a1a3a' : (SPEED_COLOR[lvl.speed] ?? '#555577')
    this.add.text(x, y + NODE_R + 34, lvl.speed.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '10px', color: speedColor,
    }).setOrigin(0.5).setDepth(3)

    // Stage progress (e.g. "7/10")
    if (!locked) {
      const stagesDone = completed ? STAGES_PER_LEVEL : (isCurrent ? p.currentStage : 0)
      const progColor = completed ? '#3a6a44' : '#888899'
      this.add.text(x, y - NODE_R - 16, `${stagesDone}/${STAGES_PER_LEVEL}`, {
        fontFamily: 'monospace', fontSize: '12px', color: progColor,
      }).setOrigin(0.5).setDepth(3)
    }

    // Checkmark for completed
    if (completed) {
      this.add.text(x + NODE_R - 6, y - NODE_R + 6, '\u2713', {
        fontFamily: 'monospace', fontSize: '18px', color: '#4ECB71',
      }).setOrigin(0.5).setDepth(4)
    }

    // Locked icon
    if (locked) {
      this.add.text(x, y - NODE_R - 16, '\u{1f512}', {
        fontFamily: 'monospace', fontSize: '12px', color: '#222244',
      }).setOrigin(0.5).setDepth(3)
    }

    // Click to enter (current or completed levels)
    if (isCurrent || completed) {
      const zone = this.add.zone(x, y, NODE_R * 2, NODE_R * 2)
        .setInteractive({ useHandCursor: true }).setDepth(5)
      zone.on('pointerdown', () => {
        this.selectedLevel = index
        this.viewMode = 'stages'
        this.scene.restart({ view: this.viewMode, level: this.selectedLevel })
      })

      if (isCurrent) {
        const mob = isTouchDevice()
        const btn = this.add.text(x, y + NODE_R + 60, 'ENTER', {
          fontFamily: 'monospace', fontSize: mob ? '22px' : '15px',
          color: '#FFE566', backgroundColor: '#1a1a00',
          padding: mob ? { x: 22, y: 10 } : { x: 14, y: 5 },
        }).setOrigin(0.5).setDepth(4).setInteractive({ useHandCursor: true })
        btn.on('pointerover', () => btn.setStyle({ color: '#ffffff' }))
        btn.on('pointerout',  () => btn.setStyle({ color: '#FFE566' }))
        btn.on('pointerdown', () => {
          this.selectedLevel = index
          this.viewMode = 'stages'
          this.scene.restart({ view: this.viewMode, level: this.selectedLevel })
        })
        this.tweens.add({ targets: btn, scaleX: 1.04, scaleY: 1.04, yoyo: true, repeat: -1, duration: 500 })
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STAGE MAP — shows 10 fight nodes within a level
  // ═══════════════════════════════════════════════════════════════════════════

  private createStageMap(): void {
    const W     = this.scale.width
    const H     = this.scale.height
    const li    = this.selectedLevel
    const lvl   = LEVELS[li]
    const p     = this.progress
    const isCurrentLevel = li === p.currentLevel
    const levelCompleted = li < p.currentLevel

    const mapKey = `map_${String(li + 1).padStart(2, '0')}`
    if (this.textures.exists(mapKey)) {
      const bg = this.add.image(W / 2, H / 2, mapKey)
      bg.setDisplaySize(W, H)
      // Darken so UI stays readable
      this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.52)
    } else {
      this.add.rectangle(W / 2, H / 2, W, H, 0x0a0a1a)
    }

    // Header
    this.add.text(W / 2, 36, `World ${li + 1} — ${lvl.name}`, {
      fontFamily: 'monospace', fontSize: '26px',
      color: '#FFE566', stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5)

    const stagesDone = levelCompleted ? STAGES_PER_LEVEL : (isCurrentLevel ? p.currentStage : 0)
    this.add.text(W / 2, 70, `${stagesDone}/${STAGES_PER_LEVEL} stages cleared  ·  Speed: ${lvl.speed.toUpperCase()}`, {
      fontFamily: 'monospace', fontSize: '12px',
      color: SPEED_COLOR[lvl.speed] ?? '#555577',
    }).setOrigin(0.5)

    // Total score
    this.add.text(W / 2, 94, `TOTAL SCORE: ${p.totalScore.toLocaleString()}`, {
      fontFamily: 'monospace', fontSize: '14px', color: '#4CC9F0',
    }).setOrigin(0.5)

    if (levelCompleted) {
      this.add.text(W / 2, 118, 'LEVEL COMPLETE!', {
        fontFamily: 'monospace', fontSize: '16px', color: '#4ECB71',
      }).setOrigin(0.5)
    }

    // Path lines
    const pathGfx = this.add.graphics()
    for (let i = 0; i < STAGES_PER_LEVEL - 1; i++) {
      const a = stagePos(i)
      const b = stagePos(i + 1)
      const done = (levelCompleted || (isCurrentLevel && i < p.currentStage))
      pathGfx.lineStyle(8, 0x080810)
      pathGfx.lineBetween(a.x, a.y, b.x, b.y)
      pathGfx.lineStyle(5, done ? 0x1a3a28 : 0x0f0f1e)
      pathGfx.lineBetween(a.x, a.y, b.x, b.y)
    }

    // Stage nodes
    for (let i = 0; i < STAGES_PER_LEVEL; i++) {
      this.drawStageNode(li, i)
    }

    // Player marker on current stage
    if (isCurrentLevel && p.currentStage < STAGES_PER_LEVEL) {
      const cp = stagePos(p.currentStage)
      const marker = this.add.graphics().setDepth(5)
      marker.lineStyle(3, 0xffffff, 0.9)
      marker.strokeCircle(cp.x, cp.y, NODE_R + 8)
      this.tweens.add({ targets: marker, alpha: 0.3, yoyo: true, repeat: -1, duration: 600 })
    }

    // Back to world map
    const mob = isTouchDevice()
    const back = this.add.text(24, 36, '< WORLD MAP', {
      fontFamily: 'monospace', fontSize: mob ? '20px' : '14px', color: '#555577',
      padding: mob ? { x: 12, y: 8 } : { x: 6, y: 3 },
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true })
    back.on('pointerover', () => back.setColor('#aaaacc'))
    back.on('pointerout',  () => back.setColor('#555577'))
    back.on('pointerdown', () => {
      this.viewMode = 'world'
      this.selectedLevel = -1
      this.scene.restart({ view: this.viewMode, level: this.selectedLevel })
    })
  }

  private drawStageNode(levelIndex: number, stageIndex: number): void {
    const pos        = stagePos(stageIndex)
    const p          = this.progress
    const lvl        = LEVELS[levelIndex]
    const isCurrentLvl = levelIndex === p.currentLevel
    const levelDone  = levelIndex < p.currentLevel
    const completed  = levelDone || (isCurrentLvl && stageIndex < p.currentStage)
    const isCurrent  = isCurrentLvl && stageIndex === p.currentStage
    const locked     = !completed && !isCurrent

    const gfx = this.add.graphics().setDepth(2)

    // Circle
    if (completed) {
      gfx.fillStyle(0x1a3a28); gfx.fillCircle(pos.x, pos.y, NODE_R)
      gfx.lineStyle(3, 0x4ECB71); gfx.strokeCircle(pos.x, pos.y, NODE_R)
    } else if (isCurrent) {
      gfx.fillStyle(0x1a1a3a); gfx.fillCircle(pos.x, pos.y, NODE_R)
      gfx.lineStyle(3, lvl.color); gfx.strokeCircle(pos.x, pos.y, NODE_R)
    } else {
      gfx.fillStyle(0x0d0d1e); gfx.fillCircle(pos.x, pos.y, NODE_R)
      gfx.lineStyle(2, 0x1a1a3a); gfx.strokeCircle(pos.x, pos.y, NODE_R)
    }

    // Villain portrait thumbnail (inside node circle)
    const wp = String(levelIndex + 1).padStart(2, '0')
    const sp = String(stageIndex + 1).padStart(2, '0')
    const vKey = `villain_${wp}_${sp}`
    if (!locked && this.textures.exists(vKey)) {
      const r = NODE_R - 2
      const portrait = this.add.image(pos.x, pos.y, vKey)
      portrait.setDisplaySize(r * 2, r * 2)
      portrait.setDepth(2)
      const maskGfx = this.make.graphics({ x: pos.x - r, y: pos.y - r })
      maskGfx.fillStyle(0xffffff)
      maskGfx.fillCircle(r, r, r)
      portrait.setMask(maskGfx.createGeometryMask())
    }

    // Stage number (only shown when locked, i.e. no portrait)
    const numColor = completed ? '#4ECB71' : isCurrent ? '#ffffff' : '#333355'
    if (locked) {
      this.add.text(pos.x, pos.y, `${stageIndex + 1}`, {
        fontFamily: 'monospace', fontSize: '20px',
        color: numColor, stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(3)
    }

    // Opponent name below
    const opponent = WORLD_OPPONENTS[levelIndex][stageIndex]
    const nameColor = completed ? '#3a8a50' : isCurrent ? '#ddddff' : '#222244'
    this.add.text(pos.x, pos.y + NODE_R + 14, opponent, {
      fontFamily: 'monospace', fontSize: '11px', color: nameColor,
    }).setOrigin(0.5).setDepth(3)

    // AI difficulty above
    const diff = lvl.difficulties[stageIndex]
    const diffColor = locked ? '#1a1a3a' : isCurrent ? '#888899' : completed ? '#3a6a44' : '#333355'
    this.add.text(pos.x, pos.y - NODE_R - 16, `AI ${diff}`, {
      fontFamily: 'monospace', fontSize: '10px', color: diffColor,
    }).setOrigin(0.5).setDepth(3)

    // Score for completed stages
    if (completed) {
      const scores = p.stageScores[levelIndex]
      const sc = scores ? scores[stageIndex] : undefined
      if (sc !== undefined) {
        this.add.text(pos.x, pos.y - NODE_R - 32, `${sc.toLocaleString()} pts`, {
          fontFamily: 'monospace', fontSize: '9px', color: '#3a6a44',
        }).setOrigin(0.5).setDepth(3)
      }
      // Checkmark
      this.add.text(pos.x + NODE_R - 4, pos.y - NODE_R + 4, '\u2713', {
        fontFamily: 'monospace', fontSize: '16px', color: '#4ECB71',
      }).setOrigin(0.5).setDepth(4)
    }

    // Clickable: current stage (FIGHT!) or completed stage (REPLAY)
    if (isCurrent) {
      const mob = isTouchDevice()
      const btn = this.add.text(pos.x, pos.y + NODE_R + 40, 'FIGHT!', {
        fontFamily: 'monospace', fontSize: mob ? '22px' : '15px',
        color: '#FFE566', backgroundColor: '#1a1a00',
        padding: mob ? { x: 22, y: 10 } : { x: 14, y: 5 },
      }).setOrigin(0.5).setDepth(4).setInteractive({ useHandCursor: true })
      btn.on('pointerover', () => btn.setStyle({ color: '#ffffff' }))
      btn.on('pointerout',  () => btn.setStyle({ color: '#FFE566' }))
      btn.on('pointerdown', () => this.startFight(levelIndex, stageIndex))
      this.tweens.add({ targets: btn, scaleX: 1.05, scaleY: 1.05, yoyo: true, repeat: -1, duration: 500 })
    } else if (completed) {
      // Allow replaying any completed stage
      const zone = this.add.zone(pos.x, pos.y, NODE_R * 2, NODE_R * 2)
        .setInteractive({ useHandCursor: true }).setDepth(5)
      zone.on('pointerdown', () => this.startFight(levelIndex, stageIndex))
    }
  }

  // ─── Start a fight ────────────────────────────────────────────────────────

  private startFight(levelIndex: number, stageIndex: number): void {
    const lvl = LEVELS[levelIndex]
    this.scene.start('CampaignScene', {
      levelIndex,
      stageIndex,
      aiDifficulty: lvl.difficulties[stageIndex],
      speedKey:     lvl.speed,
      stageName:    WORLD_OPPONENTS[levelIndex][stageIndex],
    })
  }
}
